import { useState } from "react";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";

interface BuyerFormData {
  productName: string;
  requiredQuantity: string;
  deliveryCity: string;
  buyerName: string;
  mobileNumber: string;
}

const availableProducts = [
  { id: 1, name: "Onion", grade: "A Grade", quantity: "10000 kg", priceRange: "₹18-22/kg", location: "Nashik, Maharashtra", lastUpdated: "Today" },
  { id: 2, name: "Potato", grade: "Premium", quantity: "3000 quintal", priceRange: "₹2000-2500/quintal", location: "Agra, Uttar Pradesh", lastUpdated: "Yesterday" },
  { id: 3, name: "Tomato", grade: "Fresh", quantity: "2500 kg", priceRange: "₹35-45/kg", location: "Belgaum, Karnataka", lastUpdated: "Today" },
  { id: 4, name: "Wheat", grade: "Food Grade", quantity: "1000 quintal", priceRange: "₹3000-3500/quintal", location: "Indore, Madhya Pradesh", lastUpdated: "2 days ago" },
  { id: 5, name: "Rice", grade: "Basmati", quantity: "500 quintal", priceRange: "₹4500-5500/quintal", location: "Ludhiana, Punjab", lastUpdated: "3 days ago" },
  { id: 6, name: "Garlic", grade: "Premium", quantity: "800 kg", priceRange: "₹100-120/kg", location: "Jalgaon, Maharashtra", lastUpdated: "Yesterday" },
];

const ForBuyers = () => {
  const [formData, setFormData] = useState<BuyerFormData>({
    productName: "",
    requiredQuantity: "",
    deliveryCity: "",
    buyerName: "",
    mobileNumber: "",
  });

  const [errors, setErrors] = useState<Partial<BuyerFormData>>({});

  const validateForm = (): boolean => {
    const newErrors: Partial<BuyerFormData> = {};

    if (!formData.productName.trim()) {
      newErrors.productName = "Product name is required";
    }
    if (!formData.requiredQuantity.trim()) {
      newErrors.requiredQuantity = "Quantity is required";
    }
    if (!formData.deliveryCity.trim()) {
      newErrors.deliveryCity = "Delivery city is required";
    }
    if (!formData.buyerName.trim()) {
      newErrors.buyerName = "Buyer name is required";
    }
    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (!/^[0-9]{10}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = "Invalid mobile number (10 digits required)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const message = `Buyer Enquiry – VIATRA

Product: ${formData.productName}
Quantity: ${formData.requiredQuantity}
Delivery City: ${formData.deliveryCity}
Buyer Name: ${formData.buyerName}
Mobile: ${formData.mobileNumber}`;

    const whatsappUrl = `https://wa.me/919327264950?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  return (
    <Layout>
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-6 lg:px-8">
          {/* Page Title */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              For Buyers – Buy Agricultural Products
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Connect with verified agricultural producers and source quality produce directly
            </p>
          </div>

          {/* Section 1: Intro Block */}
          <section className="mb-20">
            <Card className="p-8 md:p-12 bg-gradient-to-br from-secondary/50 to-background border-primary/20">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                How VIATRA Helps Buyers
              </h2>
              <p className="text-lg text-foreground leading-relaxed mb-4">
                VIATRA connects buyers with verified agricultural produce. View available products and send your requirement directly to our team via WhatsApp.
              </p>
              <p className="text-base text-muted-foreground">
                Our dedicated team will verify your enquiry, check product availability, and connect you with the most suitable suppliers for your specific needs.
              </p>
            </Card>
          </section>

          {/* Section 2: Available Products */}
          <section className="mb-20">
            <h2 className="text-3xl font-bold text-foreground mb-8">Available Products</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {availableProducts.map((product) => (
                <Card key={product.id} className="p-6 hover:shadow-lg transition-shadow border border-primary/20">
                  <h3 className="text-xl font-bold text-primary mb-4">{product.name}</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Grade / Quality:</span>
                      <span className="text-sm font-semibold text-foreground">{product.grade}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Available Quantity:</span>
                      <span className="text-sm font-semibold text-foreground">{product.quantity}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Price Range:</span>
                      <span className="text-sm font-semibold text-primary">{product.priceRange}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Source Location:</span>
                      <span className="text-sm font-semibold text-foreground">{product.location}</span>
                    </div>
                    <div className="pt-3 border-t border-border">
                      <span className="text-xs text-muted-foreground">Last Updated: {product.lastUpdated}</span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </section>

          {/* Section 3: Buyer Enquiry Form */}
          <section className="mb-20">
            <h2 className="text-3xl font-bold text-foreground mb-8">Request Agricultural Products</h2>
            <Card className="p-8 md:p-12 border-primary/20">
              <div className="space-y-6">
                {/* Product Name - Dropdown */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-3">
                    Product Name <span className="text-red-500">*</span>
                  </label>
                  <Select value={formData.productName} onValueChange={(value) => setFormData({ ...formData, productName: value })}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a product" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableProducts.map((product) => (
                        <SelectItem key={product.id} value={product.name}>
                          {product.name}
                        </SelectItem>
                      ))}
                      <SelectItem value="other">Other (specify in enquiry)</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.productName && <p className="text-xs text-red-500 mt-1">{errors.productName}</p>}
                </div>

                {/* Required Quantity */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-3">
                    Required Quantity <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="e.g., 1000 kg or 50 quintal"
                    value={formData.requiredQuantity}
                    onChange={(e) => setFormData({ ...formData, requiredQuantity: e.target.value })}
                    className="w-full"
                  />
                  {errors.requiredQuantity && <p className="text-xs text-red-500 mt-1">{errors.requiredQuantity}</p>}
                </div>

                {/* Delivery City */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-3">
                    Delivery City <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="e.g., Delhi, Mumbai, Bangalore"
                    value={formData.deliveryCity}
                    onChange={(e) => setFormData({ ...formData, deliveryCity: e.target.value })}
                    className="w-full"
                  />
                  {errors.deliveryCity && <p className="text-xs text-red-500 mt-1">{errors.deliveryCity}</p>}
                </div>

                {/* Buyer Name */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-3">
                    Buyer Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="Your full name"
                    value={formData.buyerName}
                    onChange={(e) => setFormData({ ...formData, buyerName: e.target.value })}
                    className="w-full"
                  />
                  {errors.buyerName && <p className="text-xs text-red-500 mt-1">{errors.buyerName}</p>}
                </div>

                {/* Mobile Number */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-3">
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="10-digit mobile number"
                    value={formData.mobileNumber}
                    onChange={(e) => setFormData({ ...formData, mobileNumber: e.target.value })}
                    className="w-full"
                  />
                  {errors.mobileNumber && <p className="text-xs text-red-500 mt-1">{errors.mobileNumber}</p>}
                </div>

                {/* Submit Button */}
                <div className="pt-6">
                  <Button
                    onClick={handleSubmit}
                    className="w-full bg-primary hover:bg-primary/90 text-white text-lg py-6 rounded-lg font-semibold transition-colors glow-gold"
                  >
                    Request Quote via WhatsApp
                  </Button>
                </div>
              </div>
            </Card>
          </section>

          {/* Section 4: Trust Note */}
          <section className="bg-secondary/30 border border-primary/20 rounded-lg p-6 md:p-8">
            <p className="text-foreground text-center">
              <span className="font-semibold">🔒 Secure & Direct Communication:</span> After submitting your enquiry, it will be sent directly to the VIATRA team via WhatsApp for faster response and transparent negotiations.
            </p>
          </section>
        </div>
      </main>
    </Layout>
  );
};

export default ForBuyers;
